import {
  File,
  FileText,
  Film,
  Image as ImageIcon,
  Link2,
  Music,
  Play,
} from "lucide-react";
import type { AssetType, EmbedProvider } from "@colab/shared";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useSignedAssetUrl } from "@/hooks/use-signed-asset-url";
import { inferFileKind } from "@/lib/file-type";

// Card thumbnail for a content asset. Images render a real preview from a
// `log=0` signed URL (no view logged); every other shape — pdf/video/office/
// other files, embeds and external links — gets a designed type placeholder
// (icon over a soft accent gradient), so the grid stays fast and consistent
// without per-file thumbnail generation.

export type FileThumbnailAsset = {
  id: string;
  fileUrl: string | null;
  assetType: AssetType;
  embedProvider: EmbedProvider | null;
  externalUrl: string | null;
};

const aspect = "aspect-video w-full overflow-hidden rounded-lg border bg-muted/30";

export function FileThumbnail({
  asset,
  className,
}: {
  asset: FileThumbnailAsset;
  className?: string;
}) {
  const isEmbed = !!asset.embedProvider;
  const isExternal = !asset.fileUrl && !isEmbed && !!asset.externalUrl;
  const kind = inferFileKind(asset.fileUrl);
  const isImage = !!asset.fileUrl && kind === "image";

  const query = useSignedAssetUrl(asset.id, { enabled: isImage, log: false });

  if (isImage) {
    if (query.isPending) {
      return <Skeleton className={cn(aspect, className)} />;
    }
    if (query.data?.url && !query.isError) {
      return (
        <div className={cn(aspect, className)}>
          <img
            src={query.data.url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      );
    }
    // fall through to placeholder on error
  }

  const Icon = isEmbed
    ? Play
    : isExternal
      ? Link2
      : kind === "image"
        ? ImageIcon
        : kind === "video"
          ? Film
          : kind === "audio"
            ? Music
            : kind === "pdf" || kind === "office"
              ? FileText
              : File;

  return (
    <div
      className={cn(
        aspect,
        "flex items-center justify-center bg-gradient-to-br from-accent/10 via-background to-muted",
        className,
      )}
    >
      <Icon className="h-8 w-8 text-accent/70" strokeWidth={1.5} />
    </div>
  );
}
